import { createFileRoute, Link, useNavigate, useRouter } from '@tanstack/react-router'
import { Alert, Button, Card, Checkbox, Form, Input, Radio, Result, Typography } from 'antd'
import React, { useState } from 'react'
import { FormItem } from '../components/form-item'
import { Trans, useTranslation } from 'react-i18next'
import useForm from '../hooks/use-form'
import { RegisterInput } from '../user/input/register-input'
import { useRegister } from '../user/hooks'
import i18n from 'i18next'
import { redirectPath, useAuth } from '../auth'
import { useTitle } from '../hooks/use-title'
import { StartChoice } from '../components/start-choice'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { fetchRegistrationSecurity } from '../user/api'
import { TurnstileCheck } from '../components/turnstile-check'

const { Text, Title } = Typography

export const Route = createFileRoute('/_guest/')({
    validateSearch: z.object({
        invitation: z.string().optional(),
        email: z.string().optional(),
    }),
    component: RegisterComponent,
})

function RegisterComponent() {
    const { t } = useTranslation()
    const { loginFromResponse } = useAuth()
    const router = useRouter()
    const navigate = useNavigate()
    const [securityReset, setSecurityReset] = useState(0)
    const register = useRegister(() => setSecurityReset((value) => value + 1))
    const security = useQuery({ queryKey: ['registration-security'], queryFn: fetchRegistrationSecurity })
    const { invitation, email, redirect } = Route.useSearch()

    const formProps = useForm({
        mutation: register,
        onSuccess: (response) => {
            loginFromResponse(response)

            // Invited users and those who came here to do something, like connecting a website, go straight on.
            if (invitation || redirect) {
                setTimeout(() => {
                    router.invalidate().finally(() => navigate({ to: redirectPath(redirect) }))
                }, 0)
            }
        },
    })

    const token = Form.useWatch('turnstileToken', formProps.form)

    useTitle(t('Create Account'))

    if (register.isSuccess) {
        if (invitation || redirect) {
            return null
        }

        return (
            <Result
                title={t('Registration successful')}
                subTitle={t('Please verify your email address by following the instructions we’ve sent to your inbox')}
                status="success"
                extra={<StartChoice />}
            />
        )
    }

    return (
        <Card className="w-full max-w-[480px]">
            <div className="mb-6">
                <Title level={2}>{t('Create Account')}</Title>
            </div>
            <Form
                {...formProps}
                onFinish={(attributes) => {
                    if (token && security.data?.turnstileSiteKey && !register.isPending)
                        formProps.onFinish?.(attributes)
                }}
                initialValues={{
                    locale: 'nl_NL',
                    email,
                    invitation,
                }}
                layout="vertical"
                autoComplete="off"
                // The server checks the email, so its error shows in the form's language, like the other errors.
                noValidate
            >
                <FormItem<RegisterInput>
                    label={t('Language')}
                    name="locale"
                    required
                >
                    <Radio.Group
                        size="large"
                        onChange={(event) => i18n.changeLanguage(event.target.value)}
                    >
                        <Radio.Button value="nl_NL">{t('locales.nl_NL')}</Radio.Button>
                        <Radio.Button value="en_US">{t('locales.en_US')}</Radio.Button>
                    </Radio.Group>
                </FormItem>
                <FormItem<RegisterInput>
                    name="invitation"
                    hidden
                >
                    <Input />
                </FormItem>
                {!invitation && (
                    <FormItem<RegisterInput>
                        label={t('Company name')}
                        name="companyName"
                        required
                    >
                        <Input placeholder="Evaa B.V." />
                    </FormItem>
                )}
                <FormItem<RegisterInput>
                    label={t('Name')}
                    name="name"
                    required
                >
                    <Input placeholder="John Doe" />
                </FormItem>

                <FormItem<RegisterInput>
                    label={t('Email')}
                    name="email"
                    required
                >
                    <Input
                        type="email"
                        disabled={!!invitation}
                        placeholder="john@example.com"
                    />
                </FormItem>

                <FormItem<RegisterInput>
                    label={t('Password')}
                    name="password"
                    required
                >
                    <Input.Password placeholder="Password" />
                </FormItem>
                <FormItem<RegisterInput>
                    label={t('Confirm password')}
                    name="passwordConfirmation"
                    required
                >
                    <Input.Password placeholder="Password" />
                </FormItem>

                <FormItem>
                    <FormItem<RegisterInput>
                        name="agreement"
                        valuePropName="checked"
                        className="mb-0"
                    >
                        <Checkbox name="agreement">
                            {/* refactor this for i18n */}
                            <Trans
                                defaults="By completing this registration, you hereby agree to our <general>General Terms and Conditions</general>, and to our <privacy>Data privacy policy</privacy>"
                                components={{
                                    general: (
                                        <a
                                            href="https://evaa.chat/algemene-voorwaarden"
                                            className="text-primary"
                                        />
                                    ),
                                    privacy: (
                                        <a
                                            href="https://evaa.chat/privacyverklaring"
                                            className="text-primary"
                                        />
                                    ),
                                }}
                            />
                        </Checkbox>
                    </FormItem>
                </FormItem>

                {security.data?.turnstileSiteKey ? (
                    <FormItem<RegisterInput>
                        className="mb-0 [&_.ant-form-item-control-input]:min-h-0"
                        name="turnstileToken"
                        trigger="onToken"
                        valuePropName="token"
                        rules={[
                            { required: true, message: t('Verification failed. Please retry the security check.') },
                        ]}
                    >
                        <TurnstileCheck
                            siteKey={security.data.turnstileSiteKey}
                            action="register"
                            showCheckingStatus={false}
                            appearance="light"
                            locale={i18n.language}
                            resetKey={securityReset}
                            onToken={() => {}}
                        />
                    </FormItem>
                ) : security.isPending ? null : (
                    <Alert
                        className="mb-4"
                        type="warning"
                        showIcon
                        message={t('Verification failed. Please retry the security check.')}
                        action={<Button onClick={() => security.refetch()}>{t('Retry')}</Button>}
                    />
                )}
                <FormItem className="mb-0">
                    <Button
                        loading={register.isPending}
                        disabled={!token || !security.data?.turnstileSiteKey}
                        block
                        type="primary"
                        htmlType="submit"
                        size="large"
                    >
                        {t(
                            !register.isPending && !token && (security.isPending || security.data?.turnstileSiteKey)
                                ? 'Checking…'
                                : 'Register'
                        )}
                    </Button>
                    <div className="mt-5 justify-center flex space-x-1">
                        <Text>{t('If you already have an account, you can')}</Text>
                        <Link
                            className="float-right text-primary"
                            to="/login"
                            search={{ redirect }}
                        >
                            {t('sign in, instead')}
                        </Link>
                    </div>
                </FormItem>
            </Form>
        </Card>
    )
}
